// =============================================================================
// BoardFormTemplates.js  (Phase 5 – Admin-Editor für Formular-Vorlagen)
// -----------------------------------------------------------------------------
// Verwaltung des datengetriebenen Formularsystems unter "Einstellungen":
//   - Wiederverwendbare System-Vorlagen anlegen/auswählen (Typ aus Registry).
//   - Felder einer Vorlage hinzufügen/entfernen (nur Registry-erlaubte Felder).
//   - PRO STUFE festlegen: Sichtbarkeit, Pflicht, Label, Reihenfolge
//     (sowie die ganze Vorlage je Stufe aus-/einblenden).
//   - Live-Vorschau des Ergebnisses je Stufe (generischer Renderer).
//
// Rein additiv: verändert keine bestehenden Formulare. Die Persistenz der
// Nutzdaten bleibt in den jeweiligen Stufen-Endpunkten.
// =============================================================================

import React, { useState, useEffect, useCallback } from "react";
import {
  Container, Card, Row, Col, Form, Table, Button, Alert, Badge, Spinner, Modal,
} from "react-bootstrap";
import { FaTrash, FaSave, FaPlus, FaSyncAlt } from "react-icons/fa";
import TemplateForm from "./TemplateForm";
import UserService from "../services/user.service";

const BoardFormTemplates = () => {
  const [registry, setRegistry] = useState({ tiers: [], templates: [] });
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [tier, setTier] = useState("MAINVO");
  const [rows, setRows] = useState({});            // field_id -> {visible,required,label,sort_order}
  const [tplHidden, setTplHidden] = useState(false); // Vorlage in dieser Stufe ausgeblendet?
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [previewKey, setPreviewKey] = useState(0);
  const [submitted, setSubmitted] = useState(null);

  // Neue-Vorlage-Modal
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newName, setNewName] = useState("");

  // Feld-hinzufügen
  const [addFieldKey, setAddFieldKey] = useState("");

  const flash = (msg) => {
    setInfo(msg);
    window.clearTimeout(flash._t);
    flash._t = window.setTimeout(() => setInfo(""), 2500);
  };

  const loadAll = useCallback(() => {
    setLoading(true);
    setError("");
    Promise.all([UserService.getFormTemplateRegistry(), UserService.getFormTemplates(false)])
      .then(([regRes, tplRes]) => {
        setRegistry(regRes.data || { tiers: [], templates: [] });
        const list = tplRes.data || [];
        setTemplates(list);
        setSelectedId((prev) => prev || (list[0] ? String(list[0].template_id) : ""));
        setLoading(false);
      })
      .catch((err) => {
        setError((err.response && err.response.data && err.response.data.message) || "Laden fehlgeschlagen.");
        setLoading(false);
      });
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadDetail = useCallback((id) => {
    if (!id) { setDetail(null); return; }
    UserService.getFormTemplate(id)
      .then((res) => setDetail(res.data))
      .catch((err) =>
        setError((err.response && err.response.data && err.response.data.message) || "Detail-Laden fehlgeschlagen.")
      );
  }, []);

  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  // Effektive Werte je Feld für die gewählte Stufe aus Basis + Override mischen.
  useEffect(() => {
    if (!detail) { setRows({}); setTplHidden(false); return; }
    const overrides = detail.overrides || [];
    const tplOv = overrides.find((o) => o.field_id === null && o.owner_tier_code === tier && o.is_active);
    setTplHidden(tplOv ? tplOv.visible === false : false);

    const next = {};
    (detail.fields || []).forEach((f) => {
      const o = overrides.find((x) => x.field_id === f.field_id && x.owner_tier_code === tier && x.is_active);
      const baseRequired = !!(f.validation && f.validation.required);
      next[f.field_id] = {
        visible: o && o.visible !== null && o.visible !== undefined ? !!o.visible : true,
        required: o && o.required !== null && o.required !== undefined ? !!o.required : baseRequired,
        label: o && o.label_key ? o.label_key : "",
        sort_order: o && o.sort_order !== null && o.sort_order !== undefined ? o.sort_order : f.sort_order,
      };
    });
    setRows(next);
  }, [detail, tier]);

  const setRow = (fieldId, patch) =>
    setRows((prev) => ({ ...prev, [fieldId]: { ...prev[fieldId], ...patch } }));

  const refreshPreview = () => setPreviewKey((k) => k + 1);

  // --- Aktionen ---

  const handleSaveRow = (field) => {
    const r = rows[field.field_id];
    UserService.upsertFormOverride(detail.template_id, {
      field_id: field.field_id,
      owner_tier_code: tier,
      visible: r.visible,
      required: r.required,
      label_key: r.label ? r.label : null,
      sort_order: r.sort_order,
    })
      .then(() => { flash(`Gespeichert: ${field.field_key}`); return loadDetail(selectedId); })
      .then(refreshPreview)
      .catch((err) => setError((err.response && err.response.data && err.response.data.message) || "Speichern fehlgeschlagen."));
  };

  const handleToggleTemplateVisible = (hide) => {
    setTplHidden(hide);
    UserService.upsertFormOverride(detail.template_id, {
      field_id: null,
      owner_tier_code: tier,
      visible: !hide,
    })
      .then(() => { flash(hide ? "Vorlage in dieser Stufe ausgeblendet." : "Vorlage in dieser Stufe sichtbar."); return loadDetail(selectedId); })
      .then(refreshPreview)
      .catch((err) => setError((err.response && err.response.data && err.response.data.message) || "Speichern fehlgeschlagen."));
  };

  const handleDeleteField = (field) => {
    if (!window.confirm(`Feld "${field.field_key}" aus der Vorlage entfernen?`)) return;
    UserService.deleteFormField(field.field_id)
      .then(() => { flash("Feld entfernt."); return loadDetail(selectedId); })
      .then(refreshPreview)
      .catch((err) => setError((err.response && err.response.data && err.response.data.message) || "Löschen fehlgeschlagen."));
  };

  const handleAddField = () => {
    if (!addFieldKey || !detail) return;
    const def = regFieldDef(addFieldKey);
    const maxOrder = (detail.fields || []).reduce((m, f) => Math.max(m, f.sort_order || 0), 0);
    UserService.addFormField(detail.template_id, {
      field_key: addFieldKey,
      field_type: def ? def.type : "text",
      label_key: addFieldKey,
      group_key: def ? def.group_key : null,
      is_pii: def ? def.is_pii : false,
      allowed_values: def && def.allowed_values ? JSON.stringify(def.allowed_values) : null,
      validation: def && def.validation ? JSON.stringify(def.validation) : null,
      sort_order: maxOrder + 10,
    })
      .then(() => { flash("Feld hinzugefügt."); setAddFieldKey(""); return loadDetail(selectedId); })
      .then(refreshPreview)
      .catch((err) => setError((err.response && err.response.data && err.response.data.message) || "Hinzufügen fehlgeschlagen."));
  };

  const handleCreateTemplate = () => {
    if (!newKey || !newName) return;
    UserService.createFormTemplate({ template_key: newKey, name: newName })
      .then((res) => {
        setShowCreate(false);
        setNewKey(""); setNewName("");
        flash("Vorlage angelegt.");
        return UserService.getFormTemplates(false).then((r) => {
          setTemplates(r.data || []);
          if (res.data && res.data.template_id) setSelectedId(String(res.data.template_id));
        });
      })
      .catch((err) => setError((err.response && err.response.data && err.response.data.message) || "Anlegen fehlgeschlagen."));
  };

  // Registry-Definition des aktuellen Vorlagentyps.
  const regTemplate = detail
    ? registry.templates.find((t) => t.template_key === detail.template_key)
    : null;
  const regFieldDef = (key) =>
    regTemplate ? regTemplate.fields.find((f) => f.field_key === key) : null;

  // Registry-Felder, die noch nicht in der Vorlage sind (für "Feld hinzufügen").
  const availableToAdd = regTemplate
    ? regTemplate.fields
        .map((f) => f.field_key)
        .filter((k) => !(detail.fields || []).some((df) => df.field_key === k))
    : [];

  // Vorlagentypen aus der Registry, die noch nicht angelegt sind.
  const createableKeys = registry.templates
    .map((t) => t.template_key)
    .filter((k) => !templates.some((t) => t.template_key === k));

  return (
    <Container fluid className="mt-3 mb-5">
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
        <h3 className="mb-0">Formular-Vorlagen</h3>
        <Button variant="outline-secondary" size="sm" onClick={loadAll}>
          <FaSyncAlt /> Neu laden
        </Button>
      </div>
      <p className="text-muted">
        Wiederverwendbare Vorlage einmal definieren und je Stufe festlegen, welche Felder
        sichtbar/pflichtig sind, in welcher Reihenfolge und mit welchem Label.
      </p>

      {loading && (
        <div className="text-center py-4"><Spinner animation="border" /> <span>Lade…</span></div>
      )}
      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
      {info && <Alert variant="success" className="py-2">{info}</Alert>}

      {!loading && (
        <>
          {/* Vorlagen-Auswahl */}
          <Card className="mb-3">
            <Card.Body>
              <Row className="g-2 align-items-end">
                <Col md={6}>
                  <Form.Label>Vorlage</Form.Label>
                  <Form.Select value={selectedId} onChange={(e) => { setSelectedId(e.target.value); setSubmitted(null); }}>
                    {templates.length === 0 && <option value="">– keine Vorlage –</option>}
                    {templates.map((t) => (
                      <option key={t.template_id} value={t.template_id}>
                        {t.name} ({t.template_key}){t.is_active ? "" : " – inaktiv"}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label>Stufe</Form.Label>
                  <Form.Select value={tier} onChange={(e) => { setTier(e.target.value); setSubmitted(null); }}>
                    {registry.tiers.map((tr) => <option key={tr} value={tr}>{tr}</option>)}
                  </Form.Select>
                </Col>
                <Col md={3} className="text-md-end">
                  <Button variant="primary" onClick={() => setShowCreate(true)} disabled={createableKeys.length === 0}>
                    <FaPlus /> Neue Vorlage
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {detail && (
            <Row>
              {/* Editor */}
              <Col xl={7} className="mb-3">
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <span>Felder &amp; Stufen-Konfiguration <Badge bg="info">{tier}</Badge></span>
                  </Card.Header>
                  <Card.Body>
                    <Form.Check
                      type="switch"
                      id="tpl-hidden-switch"
                      className="mb-3"
                      label={`Gesamte Vorlage in Stufe "${tier}" ausblenden`}
                      checked={tplHidden}
                      onChange={(e) => handleToggleTemplateVisible(e.target.checked)}
                    />

                    <div className="table-responsive">
                      <Table size="sm" bordered hover className="align-middle">
                        <thead>
                          <tr>
                            <th>Feld</th>
                            <th className="text-center">Sichtbar</th>
                            <th className="text-center">Pflicht</th>
                            <th>Label (Stufe)</th>
                            <th style={{ width: 90 }}>Reihenf.</th>
                            <th className="text-center">Aktion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detail.fields || [])
                            .slice()
                            .sort((a, b) => a.sort_order - b.sort_order)
                            .map((f) => {
                              const r = rows[f.field_id] || {};
                              return (
                                <tr key={f.field_id} className={tplHidden ? "text-muted" : ""}>
                                  <td>
                                    <div><strong>{f.field_key}</strong></div>
                                    <div className="small text-muted">
                                      {f.field_type}
                                      {f.group_key ? ` · ${f.group_key}` : ""}
                                      {f.is_pii ? " · " : ""}
                                      {f.is_pii ? <Badge bg="light" text="dark">DSGVO</Badge> : null}
                                    </div>
                                    <div className="small text-muted">Basis-Label: {f.label_key || "—"}</div>
                                  </td>
                                  <td className="text-center">
                                    <Form.Check
                                      type="checkbox"
                                      checked={!!r.visible}
                                      disabled={tplHidden}
                                      onChange={(e) => setRow(f.field_id, { visible: e.target.checked })}
                                    />
                                  </td>
                                  <td className="text-center">
                                    <Form.Check
                                      type="checkbox"
                                      checked={!!r.required}
                                      disabled={tplHidden}
                                      onChange={(e) => setRow(f.field_id, { required: e.target.checked })}
                                    />
                                  </td>
                                  <td>
                                    <Form.Control
                                      size="sm"
                                      placeholder={f.label_key || f.field_key}
                                      value={r.label || ""}
                                      disabled={tplHidden}
                                      onChange={(e) => setRow(f.field_id, { label: e.target.value })}
                                    />
                                  </td>
                                  <td>
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      value={r.sort_order != null ? r.sort_order : 0}
                                      disabled={tplHidden}
                                      onChange={(e) => setRow(f.field_id, { sort_order: parseInt(e.target.value, 10) || 0 })}
                                    />
                                  </td>
                                  <td className="text-center text-nowrap">
                                    <Button size="sm" variant="outline-primary" className="me-1" title="Speichern"
                                      disabled={tplHidden} onClick={() => handleSaveRow(f)}>
                                      <FaSave />
                                    </Button>
                                    <Button size="sm" variant="outline-danger" title="Feld entfernen"
                                      onClick={() => handleDeleteField(f)}>
                                      <FaTrash />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          {(detail.fields || []).length === 0 && (
                            <tr><td colSpan={6} className="text-center text-muted">Noch keine Felder.</td></tr>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Feld hinzufügen */}
                    <Row className="g-2 align-items-end mt-2">
                      <Col xs={8} md={9}>
                        <Form.Label className="small mb-1">Feld hinzufügen (aus erlaubten Feldern)</Form.Label>
                        <Form.Select size="sm" value={addFieldKey} onChange={(e) => setAddFieldKey(e.target.value)}>
                          <option value="">– wählen –</option>
                          {availableToAdd.map((k) => <option key={k} value={k}>{k}</option>)}
                        </Form.Select>
                      </Col>
                      <Col xs={4} md={3}>
                        <Button size="sm" variant="success" className="w-100" disabled={!addFieldKey} onClick={handleAddField}>
                          <FaPlus /> Hinzufügen
                        </Button>
                      </Col>
                    </Row>
                    {availableToAdd.length === 0 && (
                      <div className="small text-muted mt-1">Alle erlaubten Felder dieses Typs sind bereits enthalten.</div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Live-Vorschau */}
              <Col xl={5} className="mb-3">
                <Card>
                  <Card.Header>Live-Vorschau <Badge bg="info">{tier}</Badge></Card.Header>
                  <Card.Body>
                    <TemplateForm
                      key={`${detail.template_key}_${tier}_${previewKey}`}
                      templateKey={detail.template_key}
                      tier={tier}
                      submitLabel="Test absenden"
                      onSubmit={(v) => setSubmitted(v)}
                    />
                  </Card.Body>
                </Card>
                {submitted && (
                  <Card className="mt-3">
                    <Card.Header>Abgesendete Werte (Test)</Card.Header>
                    <Card.Body>
                      <pre className="small mb-0" style={{ whiteSpace: "pre-wrap" }}>
                        {JSON.stringify(submitted, null, 2)}
                      </pre>
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>
          )}
        </>
      )}

      {/* Neue-Vorlage-Modal */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton><Modal.Title>Neue Vorlage</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Vorlagentyp (Registry)</Form.Label>
            <Form.Select value={newKey} onChange={(e) => {
              setNewKey(e.target.value);
              const rt = registry.templates.find((t) => t.template_key === e.target.value);
              if (rt && !newName) setNewName(rt.name);
            }}>
              <option value="">– wählen –</option>
              {createableKeys.map((k) => {
                const rt = registry.templates.find((t) => t.template_key === k);
                return <option key={k} value={k}>{rt ? rt.name : k} ({k})</option>;
              })}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Anzeigename" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>Abbrechen</Button>
          <Button variant="primary" onClick={handleCreateTemplate} disabled={!newKey || !newName}>Anlegen</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BoardFormTemplates;
